import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import notificationService from "../../../services/notificationService";
import { useWebNotificationContext } from "../../../contexts/WebNotificationContext";

// Mobile notification card component
const MobileNotificationCard = ({ notification, isSelected, onToggleSelect, onNavigate }) => {
  return (
    <div className={`bg-white rounded-lg p-4 shadow-sm border transition ${
      notification.isRead ? "bg-gray-50" : "bg-white border-blue-200"
    }`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex-1 min-w-0">
          <div
            onClick={() => onNavigate(notification.id)}
            className="text-sm font-medium text-blue-700 hover:underline cursor-pointer line-clamp-2"
          >
            {notification.message}
          </div>
        </div>
        
        <div className="flex items-center gap-2 flex-shrink-0">
          {!notification.isRead && (
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => onToggleSelect(notification.id)}
              className="rounded border-gray-300"
            />
          )}
          <div className={`px-2 py-1 rounded-full text-xs font-semibold ${
            notification.isRead 
              ? "bg-green-100 text-green-700" 
              : "bg-red-100 text-red-600"
          }`}>
            {notification.isRead ? "Read" : "Unread"}
          </div>
        </div>
      </div>
      
      <div className="text-xs text-slate-500">
        {new Date(notification.timestamp).toLocaleString()}
      </div>
    </div>
  );
};

export default function NotificationList({ userId }) {
  const { fetchUserNotifications, markAsRead } = notificationService(); 
  const [notifications, setNotifications] = useState([]);
  const [selected, setSelected] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const navigate = useNavigate();
  const { refreshUnreadCount } = useWebNotificationContext();

  useEffect(() => {
    const load = async () => {
      try {
        const data = await fetchUserNotifications(userId);
        setNotifications(data.content || []);
      } catch (error) {
        console.error("Failed to fetch notifications:", error);
      }
    };

    load();
  }, [userId]);

  const toggleSelect = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectAll) {
      setSelected([]);
    } else {
      setSelected(notifications.filter(n => !n.isRead).map((n) => n.id));
    }
    setSelectAll(!selectAll);
  };

  const handleMarkAsRead = async (id) => {
    await markAsRead(userId, [id]);
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, isRead: true } : n))
    );
    refreshUnreadCount();
  };

  const handleMarkSelectedAsRead = async () => {
    await markAsRead(userId, selected);
    setNotifications((prev) =>
      prev.map((n) =>
        selected.includes(n.id) ? { ...n, isRead: true } : n
      )
    );
    refreshUnreadCount();
    setSelected([]);
    setSelectAll(false);
  };

  const handleBackClick = () => {
    navigate("/home");
  };

  const handleNotificationClick = (id) => {
    navigate(`/notifications/${id}`, { state: { fromNotificationList: true } });
  };

  const hasUnreadSelected = selected.some(id => 
    notifications.find(n => n.id === id && !n.isRead)
  );

  return (
    <div className="p-4 sm:p-6">
      {/* Header - responsive layout */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <h2 className="text-xl font-semibold">All Notifications</h2>
        
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
          <button
            onClick={handleBackClick}
            className="rounded-md border border-stroke px-4 py-2 text-sm font-medium text-dark hover:border-red-600 hover:bg-red-600 hover:text-white transition"
          >
            Back
          </button>
          
          <button
            onClick={toggleSelectAll}
            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 transition"
          >
            {selectAll ? "Unselect All" : "Select All"}
          </button>
          
          {hasUnreadSelected && (
            <button
              onClick={handleMarkSelectedAsRead}
              className="rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 transition"
            >
              Mark Selected as Read
            </button>
          )}
        </div>
      </div>

      {/* Mobile cards (visible on small screens only) */}
      <div className="block sm:hidden space-y-3">
        {notifications.map((notification) => (
          <MobileNotificationCard
            key={notification.id}
            notification={notification}
            isSelected={selected.includes(notification.id)}
            onToggleSelect={toggleSelect}
            onNavigate={handleNotificationClick}
          />
        ))}
        {notifications.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            No notifications found.
          </div>
        )}
      </div>

      {/* Desktop table (hidden on small screens) */}
      <div className="hidden sm:block">
        <div className="overflow-x-auto rounded-[10px] bg-white shadow-[0px_3px_8px_0px_rgba(0,0,0,0.08)]">
          <table className="w-full table-auto border border-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectAll}
                    onChange={toggleSelectAll}
                    className="rounded border-gray-300"
                  />
                </th>
                <th className="px-4 py-3 text-left font-medium text-gray-900">Message</th>
                <th className="px-4 py-3 text-left font-medium text-gray-900">Status</th>
                <th className="px-4 py-3 text-left font-medium text-gray-900">Received At</th>
              </tr>
            </thead>
            <tbody>
              {notifications.map((n) => (
                <tr
                  key={n.id}
                  className={`border-t transition ${
                    n.isRead ? "bg-gray-50" : "bg-white"
                  } hover:bg-blue-50`}
                >
                  <td className="px-4 py-3">
                    {!n.isRead && (
                      <input
                        type="checkbox"
                        checked={selected.includes(n.id)}
                        onChange={() => toggleSelect(n.id)}
                        className="rounded border-gray-300"
                      />
                    )}
                  </td>
                  <td
                    onClick={() => handleNotificationClick(n.id)}
                    className="px-4 py-3 cursor-pointer text-blue-700 hover:underline max-w-md"
                  >
                    <div className="truncate">{n.message}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      n.isRead 
                        ? "bg-green-100 text-green-700" 
                        : "bg-red-100 text-red-600"
                    }`}>
                      {n.isRead ? "Read" : "Unread"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {new Date(n.timestamp).toLocaleString()}
                  </td>
                </tr>
              ))}
              {notifications.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-gray-500">
                    No notifications found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
